import { FC, useEffect, useMemo, useState, memo, useCallback } from "react"
import { CardSkeleton, If, ListSkeleton, Table } from "../../../ui"
import Card from "./card"
import { IntegrationCell } from "./cells"
import { EmptyStateProps } from "../../empty-state"
import { ToggleView } from "../../table/toolbar"
import Select from "../../form/select"
import { useInMobileView } from "../../../hooks"
import {
  Integration,
  BBColumnDef,
  ViewType,
  SelectOptions,
  Brand,
  SelectOption
} from "../../../../types"

interface Props {
  integrations?: Integration[]
  brands?: Brand[]
  isLoading: boolean
  onSelect?: (id: string) => void
  emptyStateProps?: EmptyStateProps
}

const ALL_BRANDS_OPTION: SelectOption = { id: "all-brands", name: "All Brands" }

const IntegrationsTable: FC<Props> = memo(
  ({ integrations = [], brands = [], isLoading, onSelect }) => {
    const inMobileView = useInMobileView()

    // Memoize columns to prevent unnecessary re-renders
    const columns: BBColumnDef<Integration>[] = useMemo(
      () => [
        {
          id: "title",
          header: () => <>Name</>,
          footer: props => props.column.id,
          cell: info => (
            <IntegrationCell data={info.row.original} onSelect={onSelect} />
          )
        }
      ],
      [onSelect]
    )
    const [viewType, setViewType] = useState<ViewType>("table")
    const [selectedBrand, setSelectedBrand] = useState<string>(
      () => ALL_BRANDS_OPTION.id
    )

    const brandOptions: SelectOptions = useMemo(
      () => [
        ALL_BRANDS_OPTION,
        ...brands.map(brand => ({ id: brand.id, name: brand.name }))
      ],
      [brands]
    )

    const filteredData = useMemo(() => {
      if (selectedBrand === "all-brands") return integrations

      return integrations.filter(i => i.brand_id === selectedBrand)
    }, [integrations, selectedBrand])

    // Memoize callback functions to prevent unnecessary re-renders
    const handleBrandChange = useCallback((value: string) => {
      setSelectedBrand(value)
    }, [])

    const handleViewTypeChange = useCallback((newViewType: ViewType) => {
      setViewType(newViewType)
    }, [])

    useEffect(() => {
      if (inMobileView) setViewType("grid")
    }, [inMobileView])

    return (
      <div className="mt-6">
        <If
          condition={isLoading}
          then={
            <If
              condition={viewType === "table"}
              then={<ListSkeleton />}
              else={<CardSkeleton />}
            />
          }
          else={
            <>
              <div className="flex items-center justify-between">
                <div className="flex">
                  <Select
                    label=""
                    selected={selectedBrand}
                    data={brandOptions}
                    IDkey="id"
                    selectClassName="w-auto md:w-48"
                    setSelected={handleBrandChange}
                  />
                </div>
                {!inMobileView && (
                  <ToggleView
                    className=""
                    viewType={viewType}
                    setViewType={handleViewTypeChange}
                  />
                )}
              </div>
              <Table
                className="mt-4 -mb-6"
                data={filteredData ?? []}
                columns={columns}
                viewType={viewType}
                pageSize={10}
                searchBarVisible={false}
                renderCard={props => (
                  <Card data={props.original} onSelect={onSelect} />
                )}
                noHeader
                removeMobilePagination
              />
            </>
          }
        />
      </div>
    )
  }
)

IntegrationsTable.displayName = "IntegrationsTable"

export default IntegrationsTable
